/**
 * inventory-api — 재고 등록 화면의 API.
 *
 * 백엔드 `modules/inventory/Inventory.Types.ts`(hcltech-parts)의 DTO 를 Zod 로 비춘다. 응답 키는 DB 열 그대로(snake_case).
 *   실제 — ① 카탈로그 검색 셋(자동완성 · SKU · 등록된 사양, `/api/admin/inventory/…`, 2026-10-02) · 재고 현황 목록(`/units`, 2026-10-14 — 실물 한 장 = 한 줄).
 *   목업 — 틀(ChassisFrame) · 부품 후보(PartCandidate). 카탈로그 표가 비어(베이 20행 · 슬롯 0) 서버 라우트가 아직 없다(현민 2026-10-01: UI 기획이 먼저).
 */
import { z } from 'zod';
import { getJson, postJson, putJson } from './http';

const BASE = '/api/admin/inventory';

// ─── ① 카탈로그 검색 — 기종 · SKU · 등록된 사양 (실제) ────────────────────
export const ChassisModelViewSchema = z.strictObject({
  id:          z.number().int().positive(),
  vendor:      z.string(),
  title:       z.string(),                      // 본편(model_role = main) 제목
  doc_count:   z.number().int().nonnegative(),  // 이 모델에 실린 편 수 — "편 n" 칩(2026-10-06)
  model:       z.string().nullable(),
  generation:  z.string().nullable(),
  form_factor: z.string().nullable(),
});
export const ChassisSkuViewSchema = z.strictObject({
  id:          z.number().int().positive(),
  option_part: z.string(),
  description: z.string().nullable(),
  form_factor: z.string().nullable(),
});
export const HcltProductViewSchema = z.strictObject({
  id:          z.string(),                      // BIGSERIAL — pg 가 문자열로 준다
  hclt_number: z.string(),
  description: z.string().nullable(),
  chassis_id:  z.number().int().positive().nullable(),
  unit_count:  z.number().int().nonnegative(),  // 부모 없는 unit 중 inspecting · available · reserved 수
});
export type ChassisModelView = z.infer<typeof ChassisModelViewSchema>;
export type ChassisSkuView   = z.infer<typeof ChassisSkuViewSchema>;
export type HcltProductView  = z.infer<typeof HcltProductViewSchema>;

/** BTO 한 줄(pre_configured_model) — 기본 포함 재료(CPU · 메모리 · 컨트롤러 · PSU)를 같이 싣는다(2026-10-05) */
export const PreconfigViewSchema = z.strictObject({
  id: z.number().int().positive(), option_part: z.string(), config_type: z.string().nullable(), description: z.string().nullable(),
  cpu_count: z.number().int().nullable(), cpu_part: z.string().nullable(), memory_capacity_gb: z.number().int().nullable(), memory_dimm_count: z.number().int().nullable(),
  storage_controller: z.string().nullable(), psu_part: z.string().nullable(), psu_count: z.number().int().nullable(), region_code: z.string().nullable(), base_chassis_id: z.number().int().nullable(),
});
export type PreconfigView = z.infer<typeof PreconfigViewSchema>;

/** 자동완성 한 줄 — SKU · BTO 줄은 그 기종을 같이 싣는다(둘 다 기종에 1:1). kind 로 갈리는 합집합 */
export const CatalogMatchSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('model'), model: ChassisModelViewSchema }),
  z.strictObject({ kind: z.literal('sku'),   model: ChassisModelViewSchema, sku: ChassisSkuViewSchema }),
  z.strictObject({ kind: z.literal('bto'),   model: ChassisModelViewSchema, bto: PreconfigViewSchema }),
]);
export type CatalogMatch = z.infer<typeof CatalogMatchSchema>;

// ─── 상세 — 고른 모델명 · SKU 의 열 전부(현민 2026-10-02 "제대로 모두 보여주기"). 키 = DB 열 ───
const SPEC_COLUMNS = {                            // chassis_model 과 chassis 가 같이 갖는 제원 열
  form_factor:            z.string().nullable(),
  rack_unit:              z.number().int().nullable(),
  cpu_vendor:             z.string().nullable(),
  max_cpu_sockets:        z.number().int().nullable(),
  chipset:                z.string().nullable(),
  bmc_version:            z.string().nullable(),
  memory_type:            z.string().nullable(),
  memory_dimm_slots:      z.number().int().nullable(),
  dimm_per_socket:        z.number().int().nullable(),
  memory_max_capacity_gb: z.number().int().nullable(),
  memory_protection:      z.string().nullable(),
  psu_slot_count:         z.number().int().nullable(),
  psu_redundant:          z.boolean().nullable(),
  psu_min_watt:           z.number().int().nullable(),
  psu_max_watt:           z.number().int().nullable(),
  pcie_total_slots:       z.number().int().nullable(),
  pcie_generation:        z.number().int().nullable(),
  ocp_slot_count:         z.number().int().nullable(),
  max_gpu_single_wide:    z.number().int().nullable(),
  max_gpu_double_wide:    z.number().int().nullable(),
  embedded_nic_ports:     z.number().int().nullable(),
  embedded_nic_speed:     z.string().nullable(),
  operating_temp_max_c:   z.number().int().nullable(),
  has_tpm:                z.boolean().nullable(),
};
export const BulletinSpecSchema = z.strictObject({ section: z.string(), label: z.string(), value: z.string() });
/** 모델에 실린 편 한 줄 — 본편 · 부록 · 옛 편(2026-10-06) */
export const ModelDocViewSchema = z.strictObject({
  doc_no:     z.number().int(),
  title:      z.string().nullable(),            // 원문에 제목이 없는 옛 편이 있다
  model_role: z.enum(['main', 'supplement', 'edition']),
});
export const ChassisModelDetailSchema = z.strictObject({
  id: z.number().int().positive(), title: z.string(), vendor: z.string(),
  model: z.string().nullable(), generation: z.string().nullable(),
  ...SPEC_COLUMNS,
  specs: z.array(BulletinSpecSchema),            // 퀵스펙 절 · 라벨 · 값 줄(서버가 JSONB 를 걸러 줌)
  docs:  z.array(ModelDocViewSchema),            // 편 목록 — 본편 · 부록 · 옛 편 순
});
/** chassis.specs 한 줄 — 퀵스펙 Standard Features 의 라벨 · 값. 화면 말 "기본 사양"(2026-10-05) */
export const ChassisSpecSchema = z.strictObject({ label: z.string(), value: z.string() });
/** chassis_storage_bay 한 행 — 그 SKU 의 베이 한 자리 */
export const ChassisBayViewSchema = z.strictObject({
  id: z.number().int().positive(), bay_position: z.string().nullable(), bay_count: z.number().int().nullable(),
  bay_type: z.string().nullable(), is_default: z.boolean().nullable(), bay_raw: z.string().nullable(),
});
export const ChassisDetailSchema = z.strictObject({
  id: z.number().int().positive(), option_part: z.string(),
  spare_part: z.string().nullable(), assembly_part: z.string().nullable(), factory_part: z.string().nullable(), primary_part_type: z.string(),
  description_raw: z.string().nullable(), description: z.string().nullable(),
  vendor: z.string().nullable(), model: z.string().nullable(), generation: z.string().nullable(), sku_type: z.string().nullable(),
  ...SPEC_COLUMNS,
  max_drive_bays_total: z.number().int().nullable(),
  height_cm: z.string().nullable(), width_cm: z.string().nullable(), depth_cm: z.string().nullable(),   // NUMERIC — pg 가 문자열로 준다
  weight_min_kg: z.string().nullable(), weight_max_kg: z.string().nullable(),
  quickspec_version: z.string().nullable(), notes: z.string().nullable(), chassis_model_id: z.number().int().nullable(),
  specs: z.array(ChassisSpecSchema),      // 기본 사양. 아직 안 실린 SKU 는 []
  bays: z.array(ChassisBayViewSchema),    // 베이. 기본 것부터
});
export type BulletinSpec       = z.infer<typeof BulletinSpecSchema>;
export type ChassisSpec        = z.infer<typeof ChassisSpecSchema>;
export type ChassisBayView     = z.infer<typeof ChassisBayViewSchema>;
export type ChassisModelDetail = z.infer<typeof ChassisModelDetailSchema>;
export type ModelDocView = z.infer<typeof ModelDocViewSchema>;
export type ChassisDetail      = z.infer<typeof ChassisDetailSchema>;

const CatalogMatchRowsSchema = z.strictObject({ rows: z.array(CatalogMatchSchema) });
const ChassisSkuRowsSchema   = z.strictObject({ rows: z.array(ChassisSkuViewSchema) });
const HcltProductRowsSchema  = z.strictObject({ rows: z.array(HcltProductViewSchema) });
const PreconfigRowsSchema    = z.strictObject({ rows: z.array(PreconfigViewSchema) });

/** GET …/chassis-models/:id/preconfigs — 그 모델명의 BTO 전부(-B21 먼저). 없으면 [] */
export async function fetchChassisPreconfigs(modelId: number): Promise<PreconfigView[]> {
  const body = await getJson(`${BASE}/chassis-models/${modelId}/preconfigs`, { schema: PreconfigRowsSchema });
  return body.rows;
}

/** GET …/catalog-matches?q= — 자동완성. q 로 시작하는 SKU 먼저, model · title 에 q 가 든 기종 다음. 합쳐 10개. q 빈 글이면 서버 안 부르고 [] */
export async function fetchCatalogMatches(q: string, opts: { signal?: AbortSignal } = {}): Promise<CatalogMatch[]> {
  const trimmed = q.trim();
  if (trimmed === '') return [];
  const body = await getJson(`${BASE}/catalog-matches?q=${encodeURIComponent(trimmed)}`, { signal: opts.signal, schema: CatalogMatchRowsSchema });
  return body.rows;
}

/** GET …/chassis-models/:id — 모델 상세(열 전부 + 퀵스펙 줄). 없으면 404 → Error */
export async function fetchChassisModelDetail(modelId: number): Promise<ChassisModelDetail> {
  return getJson(`${BASE}/chassis-models/${modelId}`, { schema: ChassisModelDetailSchema });
}

/** GET …/chassis/:id — SKU 상세(chassis 열 전부 + 기본 사양 + 베이). 없으면 404 → Error */
export async function fetchChassisDetail(chassisId: number): Promise<ChassisDetail> {
  return getJson(`${BASE}/chassis/${chassisId}`, { schema: ChassisDetailSchema });
}

/** GET …/chassis-models/:id/skus — 그 모델명의 CTO SKU 전부. 없으면 [] */
export async function fetchChassisSkus(modelId: number): Promise<ChassisSkuView[]> {
  const body = await getJson(`${BASE}/chassis-models/${modelId}/skus`, { schema: ChassisSkuRowsSchema });
  return body.rows;
}

/** GET …/chassis-models/:id/products — 그 기종으로 등록된 사양 + 재고 수. 없으면 [] */
export async function fetchServerProducts(modelId: number): Promise<HcltProductView[]> {
  const body = await getJson(`${BASE}/chassis-models/${modelId}/products`, { schema: HcltProductRowsSchema });
  return body.rows;
}

// ─── 열거 — 백엔드와 같은 철자 ────────────────────────────────
export const E_BAY_TYPES = ['SFF', 'LFF', 'NVMe', 'EDSFF'] as const;
export type  EBayType   = typeof E_BAY_TYPES[number];

// ─── 틀 — 카탈로그가 주는 "이 서버의 자리들" ─────────────────────
export const BayGroupSchema = z.strictObject({
  position:       z.string(),
  count:          z.number().int().positive(),
  type:           z.enum(E_BAY_TYPES),
  is_default:     z.boolean(),
  enabling_parts: z.array(z.string()),
});
export const PcieSlotDefSchema = z.strictObject({
  number:        z.number().int().positive(),
  riser:         z.string(),
  form_factor:   z.string(),
  requires_proc: z.number().int().positive(),
  is_default:    z.boolean(),
  riser_part:    z.string().nullable(),
});
export const ChassisFrameSchema = z.strictObject({
  cpu_sockets:                 z.number().int().positive(),
  dimm_slots:                  z.number().int().positive(),
  dimm_per_socket:             z.number().int().positive(),
  memory_type:                 z.string(),
  bays:                        z.array(BayGroupSchema),
  pcie:                        z.array(PcieSlotDefSchema),
  ocp_slots:                   z.number().int().nonnegative(),
  psu_slots:                   z.number().int().positive(),
  controller_slots:            z.number().int().nonnegative(),
  embedded_nic:                z.string(),
  bmc:                         z.string(),
  embedded_storage_controller: z.string().nullable(),
});
export type BayGroup     = z.infer<typeof BayGroupSchema>;
export type PcieSlotDef  = z.infer<typeof PcieSlotDefSchema>;
export type ChassisFrame = z.infer<typeof ChassisFrameSchema>;

// ─── 부품 후보 — 고르기 목록 · 칸의 부품 한 장 재료 ─────────────────
export const PartCandidateSchema = z.strictObject({
  vendor:      z.string(),
  option_part: z.string(),
  category:    z.string(),                 // SPEC_CATEGORIES 19종 철자
  description: z.string(),
  serial:      z.string().nullable(),
  unit_id:     z.number().int().positive().nullable(),   // 있으면 창고의 그 unit, null 이면 저장 때 새 unit
});
export type PartCandidate = z.infer<typeof PartCandidateSchema>;

// ─── 목업 응답 — DL380 Gen10 8SFF (868703-B21) ─────────────────────
/** 틀. 베이 일곱(Box1 기본, 나머지 옵션 — 00_seed_guidelines 463~469) · Primary 라이저 3슬롯 · Secondary 옵션 · OCP 1 · PSU 2 · 소켓 2 · DIMM 24 */
export function mockChassisFrame(): ChassisFrame {
  return ChassisFrameSchema.parse({
    cpu_sockets: 2, dimm_slots: 24, dimm_per_socket: 12, memory_type: 'DDR4',
    bays: [
      { position: 'Front-Box1',     count: 8, type: 'SFF', is_default: true,  enabling_parts: [] },
      { position: 'Front-Box2',     count: 8, type: 'SFF', is_default: false, enabling_parts: ['826691-B21'] },
      { position: 'Front-Box3',     count: 8, type: 'SFF', is_default: false, enabling_parts: ['826691-B21'] },
      { position: 'MidTray',        count: 4, type: 'LFF', is_default: false, enabling_parts: ['826686-B21'] },
      { position: 'Rear-Primary',   count: 2, type: 'SFF', is_default: false, enabling_parts: ['826688-B21'] },
      { position: 'Rear-Secondary', count: 2, type: 'SFF', is_default: false, enabling_parts: ['826688-B21'] },
    ],
    pcie: [
      { number: 1, riser: 'Primary',   form_factor: 'FH-FL', requires_proc: 1, is_default: true,  riser_part: null },
      { number: 2, riser: 'Primary',   form_factor: 'FH-FL', requires_proc: 1, is_default: true,  riser_part: null },
      { number: 3, riser: 'Primary',   form_factor: 'FH-HL', requires_proc: 1, is_default: true,  riser_part: null },
      { number: 4, riser: 'Secondary', form_factor: 'FH-FL', requires_proc: 2, is_default: false, riser_part: '826694-B21' },
      { number: 5, riser: 'Secondary', form_factor: 'FH-FL', requires_proc: 2, is_default: false, riser_part: '826694-B21' },
      { number: 6, riser: 'Secondary', form_factor: 'FH-HL', requires_proc: 2, is_default: false, riser_part: '826694-B21' },
    ],
    ocp_slots: 1, psu_slots: 2, controller_slots: 1,
    embedded_nic: '4×1GbE (369i)', bmc: 'iLO5', embedded_storage_controller: 'S100i',
  });
}

/** 후보 열 장 — 재고(unit 있음) 여섯 · 검색(새 unit) 넷 */
export function mockPartCandidates(): PartCandidate[] {
  const rows: PartCandidate[] = [
    { vendor: 'HPE', option_part: 'P02510-B21', category: 'cpu',             description: 'Intel Xeon-Gold 6248R (3.0GHz/24-core/205W)',  serial: null,       unit_id: 388 },
    { vendor: 'HPE', option_part: 'P02510-B21', category: 'cpu',             description: 'Intel Xeon-Gold 6248R (3.0GHz/24-core/205W)',  serial: null,       unit_id: 389 },
    { vendor: 'HPE', option_part: 'P03050-091', category: 'memory',          description: 'HPE 16GB 2Rx8 PC4-2933Y-R Smart Kit',          serial: '8A1B2C3D', unit_id: 4021 },
    { vendor: 'HPE', option_part: 'P03050-091', category: 'memory',          description: 'HPE 16GB 2Rx8 PC4-2933Y-R Smart Kit',          serial: null,       unit_id: 4022 },
    { vendor: 'HPE', option_part: 'P18434-B21', category: 'ssd',             description: 'HPE 960GB SATA RI SFF SC MV SSD',              serial: 'S4XQNA0N', unit_id: 5120 },
    { vendor: 'HPE', option_part: '865414-B21', category: 'power_supply',    description: 'HPE 800W Flex Slot Platinum Hot Plug PSU',     serial: null,       unit_id: 7001 },
    { vendor: 'HPE', option_part: '804331-B21', category: 'raid_controller', description: 'HPE Smart Array P408i-a SR Gen10 Controller',  serial: null,       unit_id: null },
    { vendor: 'HPE', option_part: 'P08437-B21', category: 'network_adapter', description: 'HPE Ethernet 10/25Gb 2-port 640FLR-SFP28 OCP', serial: null,       unit_id: null },
    { vendor: 'HPE', option_part: '826691-B21', category: 'drive_tray',      description: 'HPE DL38X Gen10 SFF Box1/2 Cage/Backplane Kit', serial: null,      unit_id: null },
    { vendor: 'HPE', option_part: '733660-B21', category: 'rail_kit',        description: 'HPE 2U SFF Easy Install Rail Kit',             serial: null,       unit_id: null },
  ];
  return rows.map((r) => PartCandidateSchema.parse(r));
}

// ─── 재고 현황 목록 — 실물 한 장 = 한 줄 (실제 · V장 "재고 현황 목록" · 2026-10-14) ────────
/** 실물 상태 — 검수 중 → 가용 → 세팅됨 → 출고됨 / 임대 / retired · 가용 → 자산(내부 사용, 2026-10-29). 백엔드 InventoryUnit.Entity 와 같은 철자 */
export const E_STOCK_STATUSES = ['inspecting', 'available', 'reserved', 'dispatched', 'rented', 'asset', 'retired'] as const;
export type  EStockStatus     = typeof E_STOCK_STATUSES[number];

/** 생긴 근거 — INSERT 때 한 번 */
export const E_UNIT_ORIGINS = ['purchased', 'legacy', 'disassembled', 'assembled', 'found'] as const;
export type  EUnitOrigin    = typeof E_UNIT_ORIGINS[number];

/** 나간 장 — show_gone 이 꺼져 있으면 안 보인다(rented 는 늘 보인다 — 우리 자산) */
export const GONE_STATUSES: readonly EStockStatus[] = ['dispatched', 'retired'];

/** 한 줄 — inventory_unit 열 열셋 + 계산 열 일곱(product 에서 다섯 · child_count · quote_no). id 들은 BIGINT 라 글 */
export const UnitViewSchema = z.strictObject({
  id: z.string(),
  hclt_product_id: z.string(),
  stock_status: z.enum(E_STOCK_STATUSES),
  condition: z.string(),
  serial_number: z.string().nullable(),
  warehouse: z.string(),
  origin: z.enum(E_UNIT_ORIGINS),
  parent_unit_id: z.string().nullable(),
  cost_amount: z.number().nullable(),            // NUMERIC — 백엔드 core/db.ts 가 Number 로 읽는다
  cost_currency: z.string().nullable(),
  ecount_item_code: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  is_held: z.boolean(),                          // 홀딩 — 온오프. 상태가 아니다, 가용 수에서만 빠진다(2026-10-20)
  hold_note: z.string().nullable(),              // 홀딩 메모 한 줄. 켜면 있고 끄면 null
  asset_note: z.string().nullable(),             // 자산 메모 — 누가 · 어디서 쓰나. 자산이면 있고 아니면 null(2026-10-29)
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  spec_category: z.string().nullable(),
  product_type: z.enum(['part', 'server', 'set']),
  hclt_number: z.string(),
  child_count: z.number().int(),
  quote_no: z.string().nullable(),
});
export type UnitView = z.infer<typeof UnitViewSchema>;

export const UnitViewListSchema = z.strictObject({
  rows: z.array(UnitViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type UnitViewList = z.infer<typeof UnitViewListSchema>;

/** 실물 한 장의 이력 한 줄 — inventory_event(트리거가 쓴 것) */
/** 백엔드 E_INVENTORY_EVENT_TYPES · E_INVENTORY_EVENT_REASONS 와 같은 철자(328) */
export const E_INVENTORY_EVENT_TYPES = ['created', 'inspected', 'transferred', 'reserved', 'dispatched', 'rented', 'asset', 'returned', 'retired', 'changed'] as const;
export type EInventoryEventType = typeof E_INVENTORY_EVENT_TYPES[number];
export const E_INVENTORY_EVENT_REASONS = ['stocktake', 'regraded', 'typo', 'cost', 'assembled', 'disassembled', 'merged', 'hold', 'etc'] as const;
export type EInventoryEventReason = typeof E_INVENTORY_EVENT_REASONS[number];

export const UnitEventViewSchema = z.strictObject({
  id: z.string(),
  type: z.enum(E_INVENTORY_EVENT_TYPES),
  column_name: z.string().nullable(),
  old_value: z.string().nullable(),
  new_value: z.string().nullable(),
  reason: z.enum(E_INVENTORY_EVENT_REASONS).nullable(),
  reason_note: z.string().nullable(),
  changed_by: z.string().nullable(),
  created_at: z.string(),
});
export type UnitEventView = z.infer<typeof UnitEventViewSchema>;

// ─── 변동표 — 이벤트 한 줄 = 한 줄 (백엔드 V장 "재고 변동표", 2026-10-20) ───
export const UnitEventLineViewSchema = UnitEventViewSchema.extend({
  unit_id: z.string(),
  hclt_product_id: z.string(),
  hclt_number: z.string(),
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  serial_number: z.string().nullable(),
  condition: z.string(),
  origin: z.enum(E_UNIT_ORIGINS),
  stocktake_id: z.string().nullable(),
});
export type UnitEventLineView = z.infer<typeof UnitEventLineViewSchema>;
export const UnitEventLineViewListSchema = z.strictObject({
  rows: z.array(UnitEventLineViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type UnitEventLineViewList = z.infer<typeof UnitEventLineViewListSchema>;
/** '' = 안 거름. from · to 는 'YYYY-MM-DD'(KST) */
export type UnitEventListQuery = { q: string; types: EInventoryEventType[]; reason: EInventoryEventReason | ''; from: string; to: string; product_id: string; stocktake_id: string; offset: number };

/** 변동표 한 묶음 — 서버가 거른다(최신순). 빈 칸은 안 보낸다 */
export async function fetchUnitEvents(query: UnitEventListQuery): Promise<UnitEventLineViewList> {
  const params = new URLSearchParams({ limit: String(UNIT_LIST_LIMIT), offset: String(query.offset) });
  if (query.q !== '') params.set('q', query.q);
  if (query.types.length > 0) params.set('type', query.types.join(','));   // 쉼표로 여럿(2026-10-26 — 옛 한 값)
  if (query.reason !== '') params.set('reason', query.reason);
  if (query.from !== '') params.set('from', query.from);
  if (query.to !== '') params.set('to', query.to);
  if (query.product_id !== '') params.set('product_id', query.product_id);
  if (query.stocktake_id !== '') params.set('stocktake_id', query.stocktake_id);
  return getJson(`${BASE}/unit-events?${params.toString()}`, { schema: UnitEventLineViewListSchema });
}

/** 실물 상세 — 목록 한 줄 + 상세 열(번호 · 묶인 행 · 퇴역 이유) + 자식 · 부모 · 이력(현민 2026-10-15 · 새 탭 페이지) */
export const UnitDetailSchema = UnitViewSchema.extend({
  wwn: z.string().nullable(),
  psid: z.string().nullable(),
  mac: z.string().nullable(),
  manufacture_date: z.string().nullable(),
  manufacturer: z.string().nullable(),
  quote_item_id: z.string().nullable(),
  retired_reason: z.string().nullable(),
  children: z.array(UnitViewSchema),
  parent: UnitViewSchema.nullable(),
  events: z.array(UnitEventViewSchema),
});
export type UnitDetail = z.infer<typeof UnitDetailSchema>;

/** 실물 한 장 — 없으면 getJson 이 'HTTP 404 …' 로 던진다 */
/** POST /units/:id/hold — 홀딩 켜기(메모 한 줄 필수). 상태가 안 맞거나 이미 홀딩이면 서버 409 → 던진다. 바뀐 뒤의 실물 상세 */
export async function holdUnit(id: string, note: string): Promise<UnitDetail> {
  return postJson(`${BASE}/units/${encodeURIComponent(id)}/hold`, { note }, UnitDetailSchema);
}

/** POST /units/:id/release — 홀딩 끄기. 홀딩이 아니면 409 → 던진다 */
export async function releaseUnit(id: string): Promise<UnitDetail> {
  return postJson(`${BASE}/units/${encodeURIComponent(id)}/release`, {}, UnitDetailSchema);
}

/** POST /units/:id/asset — 자산으로 돌림(가용 → 자산, 메모 한 줄 필수). 가용 아님 · 홀딩 · 서버 안 부품이면 409 → 던진다(2026-10-29) */
export async function moveUnitToAsset(id: string, note: string): Promise<UnitDetail> {
  return postJson(`${BASE}/units/${encodeURIComponent(id)}/asset`, { note }, UnitDetailSchema);
}

/** POST /units/:id/asset-return — 자산에서 회수(자산 → 검수 중). 자산이 아니면 409 → 던진다 */
export async function returnUnitFromAsset(id: string): Promise<UnitDetail> {
  return postJson(`${BASE}/units/${encodeURIComponent(id)}/asset-return`, {}, UnitDetailSchema);
}

// ─── 재고 조정 — 사용 불가 · 다시 찾음 · 시리얼로 찾기 (현민 2026-11-03 · 백엔드 V장 "재고 조정") ───
/** 사용 불가 사유 — 분실 · 파손 · 폐기 · 기타(글 필수). 서버 328 CHECK 와 같은 넷 */
export const E_RETIRED_REASONS = ['lost', 'damaged', 'disposed', 'etc'] as const;
export type  ERetiredReason     = typeof E_RETIRED_REASONS[number];

/** 사용 불가 한 번 — 여러 장을 같은 사유로. 기타면 note 필수(빈 글은 null) */
export type UnitRetireInput = { unit_ids: string[]; retired_reason: ERetiredReason; note: string | null };
const UnitRetireResultSchema = z.strictObject({ unit_ids: z.array(z.string()) });

/** POST /units/retire — 사용 불가. 한 장이라도 막히면(예약 · 서버 안 부품 · 나간 장) 409 로 전부 안 옮긴다 → 던진다. 옮긴 장의 id */
export async function retireUnits(input: UnitRetireInput): Promise<string[]> {
  const result = await postJson(`${BASE}/units/retire`, input, UnitRetireResultSchema);
  return result.unit_ids;
}

/** POST /units/:id/restore — 다시 찾음(사용 불가 → 검수 중). 사용 불가가 아니거나 서버 안 부품이면 409 → 던진다. 바뀐 뒤의 실물 상세 */
export async function restoreUnit(id: string): Promise<UnitDetail> {
  return postJson(`${BASE}/units/${encodeURIComponent(id)}/restore`, {}, UnitDetailSchema);
}

/** 수량 조정 — 제품 하나 × 컨디션 하나의 수를 맞춘다(재고 상세, 2026-11-03). 빼기 · 넣기(실사 발견) · 컨디션 변경 */
export const E_STOCK_ADJUST_KINDS = ['remove', 'add', 'regrade'] as const;
export type  EStockAdjustKind     = typeof E_STOCK_ADJUST_KINDS[number];
type StockAdjustBase = { hclt_product_id: string; condition: string; quantity: number; note: string | null };
// 빼기 · 컨디션 변경은 로트(구매 줄) 하나를 고른다 — line_key 필수(백엔드 V장 "재고 조정 — 로트를 고른다", 2026-11-04)
export type StockAdjustInput = StockAdjustBase & (
  | { kind: 'remove'; retired_reason: ERetiredReason; line_key: string }
  | { kind: 'add' }
  | { kind: 'regrade'; to_condition: string; line_key: string }
);
const StockAdjustResultSchema = z.strictObject({ unit_ids: z.array(z.string()) });

/** POST /stock-adjustments — 장은 서버가 고른다. 모자라면 409(한 장도 안 바뀜) → 던진다. 바뀐 장의 id */
export async function adjustStock(input: StockAdjustInput): Promise<string[]> {
  const result = await postJson(`${BASE}/stock-adjustments`, input, StockAdjustResultSchema);
  return result.unit_ids;
}

export async function fetchUnitDetail(id: string): Promise<UnitDetail> {
  return getJson(`${BASE}/units/${encodeURIComponent(id)}`, { schema: UnitDetailSchema });
}

/** 목록 질의 — 상태 · 검색 · 나간 장 보이기 + 페이지 시작. '' = 전부(서버가 null 로 읽는다) */
export type UnitListQuery = {
  status: EStockStatus | '';
  q: string;
  show_gone: boolean;
  product_id: string;               // '' = 전체, 아니면 그 product 의 실물만(제품 상세의 링크)
  offset: number;
};

export const UNIT_LIST_LIMIT = 500;   // 한 페이지 500 — 제품 목록과 같다

/** 재고 현황 한 묶음 — 서버가 거른다. 빈 칸은 안 보낸다 */
export async function fetchUnits(query: UnitListQuery): Promise<UnitViewList> {
  const params = new URLSearchParams({ limit: String(UNIT_LIST_LIMIT), offset: String(query.offset) });
  if (query.status !== '') params.set('status', query.status);
  if (query.q !== '') params.set('q', query.q);
  if (query.show_gone) params.set('show_gone', '1');
  if (query.product_id !== '') params.set('product_id', query.product_id);
  return getJson(`${BASE}/units?${params.toString()}`, { schema: UnitViewListSchema });
}

// ─── 재고 현황 구매 건 — 구매 줄 하나 = 한 줄 (hcltech-parts V장 "재고 현황 — 구매 줄 하나 = 한 줄" · 2026-10-19) ────────
/** 줄이 어디서 왔나 — 이카운트 구매 줄 · 우리 PO 줄 · 구매 기록 없음. 백엔드와 같은 철자 */
export const E_STOCK_LINE_SOURCES = ['ecount', 'po', 'none'] as const;
export type  EStockLineSource     = typeof E_STOCK_LINE_SOURCES[number];

/** 한 줄 — 구매 줄 하나와 그 줄을 가리키는 실물의 수. 수량 셋: 산(그 구매 줄의 quantity) ≥ 보유 ≥ 가용 */
export const StockLineViewSchema = z.strictObject({
  line_key: z.string(),                          // 'ecount:<전표>:<순번>' · 'po:<PO 줄 id>' · 'none:<제품>:<컨디션>:<출처>' — 줄의 열쇠(2026-10-26 실물 탭 거르기는 뺌 — 재고 상세에 실물이 있다)
  source: z.enum(E_STOCK_LINE_SOURCES),
  purchased_date: z.string().nullable(),         // 'YYYY-MM-DD'. 기록 없음은 null
  supplier_name: z.string().nullable(),
  purchase_no: z.string().nullable(),            // 이카운트 전표번호 · PO 번호
  hclt_product_id: z.string(),
  hclt_number: z.string(),
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  spec_category: z.string().nullable(),
  product_type: z.enum(['part', 'server', 'set']),
  condition: z.string(),
  origin: z.enum(E_UNIT_ORIGINS),
  unit_price: z.number().nullable(),             // 이카운트 단가 · PO unit_cost. 빈 단가 · 기록 없음은 null
  cost_currency: z.string().nullable(),
  // 수량 넷 — 전부 이 로트(구매 줄) 하나의 수. 입고 = 출고 + 사용 중 + 사용 가능(2026-10-29 · 회의 2026-10-28)
  inbound_count: z.number().int(),               // 입고 — 구매 수량. 구매 기록 없는 줄은 실물 수
  outbound_count: z.number().int(),              // 출고 — 입고 − 사용 중 − 사용 가능(이카운트 옮기기 전에 나간 장도 든다)
  in_use_count: z.number().int(),                // 사용 중 — 검수 중 · 예약 · 임대 · 자산 · 사용 불가 · 가용인데 홀딩
  usable_count: z.number().int(),                // 사용 가능 — 가용 · 홀딩 아님
  vendor: z.string().nullable(),                         // 제조사 — 제품 목록과 같은 규칙(2026-10-26)
  buyer_name: z.string().nullable(),                     // 담당자 — 우리 PO 줄만, 이카운트 줄은 null
});
export type StockLineView = z.infer<typeof StockLineViewSchema>;

export const StockLineViewListSchema = z.strictObject({
  rows: z.array(StockLineViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type StockLineViewList = z.infer<typeof StockLineViewListSchema>;

/** 목록(옛 구매 건) 질의 — 찾기(파트넘버 · 디스크립션 · 구매처) · 제품 · 카테고리 · 구매일 기간 + 페이지 시작. '' = 전부. 보유 1장 이상인 줄만 온다(다 나간 줄은 구매 이력의 몫) */
export type StockLineListQuery = { q: string; product_id: string; include_gone: boolean; category: string; from: string; to: string; offset: number };   // category · from · to — 카테고리 · 구매일 기간('YYYY-MM-DD', '' = 전부. 2026-10-28)   // include_gone — 다 나간 구매 줄까지(재고 상세의 입고, 2026-10-26). 구매 건 탭은 false

/** 구매 건 한 묶음 — 서버가 거른다. 빈 칸은 안 보낸다 */
export async function fetchStockLines(query: StockLineListQuery): Promise<StockLineViewList> {
  const params = new URLSearchParams({ limit: String(UNIT_LIST_LIMIT), offset: String(query.offset) });
  if (query.q !== '') params.set('q', query.q);
  if (query.product_id !== '') params.set('product_id', query.product_id);
  if (query.include_gone) params.set('include_gone', '1');
  if (query.category !== '') params.set('category', query.category);
  if (query.from !== '') params.set('from', query.from);
  if (query.to !== '') params.set('to', query.to);
  return getJson(`${BASE}/stock-lines?${params.toString()}`, { schema: StockLineViewListSchema });
}

// ─── 안전재고 — 제품 하나 = 한 줄 (hcltech-parts V장 "안전재고 — 재고 쪽 설정" · 2026-10-19) ────────
/** 한 줄 — 제품 여섯 + 안전재고 + 실물 수 둘 + 모자람(안전재고 − 가용, 0 이하 · 안전재고 없음은 0) */
export const SafetyStockViewSchema = z.strictObject({
  hclt_product_id: z.string(),
  hclt_number: z.string(),
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  spec_category: z.string().nullable(),
  product_type: z.enum(['part', 'server', 'set']),
  safety_stock: z.number().int().nullable(),     // null = 안 본다
  held_count: z.number().int(),
  available_count: z.number().int(),
  shortage: z.number().int(),
});
export type SafetyStockView = z.infer<typeof SafetyStockViewSchema>;

export const SafetyStockViewListSchema = z.strictObject({
  rows: z.array(SafetyStockViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type SafetyStockViewList = z.infer<typeof SafetyStockViewListSchema>;

/** 안전재고 질의 — 찾기(파트넘버 · 품목) · 모자란 것만 + 페이지 시작. '' = 전부 */
export type SafetyStockListQuery = { q: string; only_short: boolean; offset: number };

/** 안전재고 한 묶음 — 서버가 거르고 모자란 것이 위로 온다 */
export async function fetchSafetyStocks(query: SafetyStockListQuery): Promise<SafetyStockViewList> {
  const params = new URLSearchParams({ limit: String(UNIT_LIST_LIMIT), offset: String(query.offset) });
  if (query.q !== '') params.set('q', query.q);
  if (query.only_short) params.set('only_short', '1');
  return getJson(`${BASE}/safety-stocks?${params.toString()}`, { schema: SafetyStockViewListSchema });
}

// ─── 변동표 — 기간 요약, 제품 × 컨디션 한 줄 (백엔드 V장 "재고 변동표 — 기간 요약", 2026-10-20 · 이카운트 재고변동표 꼴) ───
export const StockFlowViewSchema = z.strictObject({
  hclt_product_id: z.string(),
  hclt_number: z.string(),
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  spec_category: z.string().nullable(),
  product_type: z.enum(['part', 'server', 'set']),
  condition: z.string(),
  opening: z.number().int(),    // 기초재고 — 시작 전날 끝의 보유
  inbound: z.number().int(),    // 입고 — 기간 안 +1 사건(입고 · 반품)
  outbound: z.number().int(),   // 출고 — −1 사건(출고 · 임대 · 이탈)
  closing: z.number().int(),    // 기말재고 — 끝의 보유. 기초 + 입고 − 출고
});
export type StockFlowView = z.infer<typeof StockFlowViewSchema>;
export const StockFlowViewListSchema = z.strictObject({
  rows: z.array(StockFlowViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type StockFlowViewList = z.infer<typeof StockFlowViewListSchema>;
/** from · to 는 'YYYY-MM-DD'(KST) — 둘 다 늘 보낸다(서버 필수). only_moved = 입고 · 출고가 0 이 아닌 줄만 */
export type StockFlowListQuery = { q: string; from: string; to: string; only_moved: boolean; offset: number };

/** 변동표 한 묶음 — 서버가 지금 수에서 사건을 되감아 센다. 파트넘버 · 컨디션 순 */
export async function fetchStockFlows(query: StockFlowListQuery): Promise<StockFlowViewList> {
  const params = new URLSearchParams({ from: query.from, to: query.to, limit: String(UNIT_LIST_LIMIT), offset: String(query.offset) });
  if (query.q !== '') params.set('q', query.q);
  if (query.only_moved) params.set('only_moved', '1');
  return getJson(`${BASE}/stock-flows?${params.toString()}`, { schema: StockFlowViewListSchema });
}

// ─── 일별 — 제품 × 컨디션 하나를 날짜 축으로 (백엔드 V장 "재고 일별 — 날짜 하나 = 한 줄", 2026-10-21 · A1 + B1) ───
export const StockFlowDayViewSchema = z.strictObject({
  date: z.string(),             // 'YYYY-MM-DD' KST
  opening: z.number().int(),    // 기초재고 — 그 날 0시의 보유
  inbound: z.number().int(),
  outbound: z.number().int(),
  closing: z.number().int(),    // 기말재고 — 그 날 끝의 보유 = 기초 + 입고 − 출고
});
export type StockFlowDayView = z.infer<typeof StockFlowDayViewSchema>;
export const StockFlowDayListSchema = z.strictObject({
  product: z.strictObject({
    hclt_product_id: z.string(),
    hclt_number: z.string(),
    option_part: z.string().nullable(),
    description: z.string().nullable(),
    spec_category: z.string().nullable(),
    product_type: z.enum(['part', 'server', 'set']),
  }),
  condition: z.string(),
  from: z.string(),
  to: z.string(),
  rows: z.array(StockFlowDayViewSchema),   // from ~ to 날마다 한 줄(빈 날도) · 오름차순 · 쪽 없음
});
export type StockFlowDayList = z.infer<typeof StockFlowDayListSchema>;
/** 넷 다 서버 필수. product_id '' = 아직 안 고름(변동표 줄의 [일별] 로 온다) — 그땐 부르지 않는다 */
export type StockFlowDayQuery = { product_id: string; condition: string; from: string; to: string };

/** 일별 한 묶음 — 제품 × 컨디션 하나의 날짜 줄 전부. 제품 없으면 404 · 366일 넘으면 400 */
export async function fetchStockFlowDays(query: StockFlowDayQuery): Promise<StockFlowDayList> {
  const params = new URLSearchParams({ product_id: query.product_id, condition: query.condition, from: query.from, to: query.to });
  return getJson(`${BASE}/stock-flow-days?${params.toString()}`, { schema: StockFlowDayListSchema });
}

/** 재고 상세의 총합 — 제품 하나(× 컨디션)의 상태별 수(hcltech-parts V장 "재고 상세 — 총합" · 2026-10-28). 부모 없는 실물만, 입고예정만 PO 줄 */
export const StockSummaryViewSchema = z.strictObject({
  hclt_product_id: z.string(),
  condition: z.string().nullable(),   // null = 컨디션 전부
  registered: z.number().int(),       // 전체 등록 — 실물 전부
  current: z.number().int(),          // 보유 — 전체 − 출고
  incoming: z.number().int(),         // 입고예정 — 열린 PO 줄의 남은 수량
  available: z.number().int(),        // 가용 — available · 홀딩 아님
  preparing: z.number().int(),        // 준비 — 판매 등록 판매에 묶인 실물(2026-11-03)
  outgoing: z.number().int(),         // 출고 예정 — 출고 요청 · 세팅 완료 판매에 묶인 실물
  rented: z.number().int(),
  asset: z.number().int(),           // 자산 — 내부에서 쓰는 장(2026-10-29)
  dispatched: z.number().int(),
  retired: z.number().int(),          // 불용 — retired 전부
  held: z.number().int(),             // 홀딩 — is_held
});
export type StockSummaryView = z.infer<typeof StockSummaryViewSchema>;

/** 총합 — condition '' = 전부. 제품 없으면 404 */
export async function fetchStockSummary(productId: string, condition: string): Promise<StockSummaryView> {
  const params = new URLSearchParams({ product_id: productId });
  if (condition !== '') params.set('condition', condition);
  return getJson(`${BASE}/stock-summary?${params.toString()}`, { schema: StockSummaryViewSchema });
}

/** 안전재고 한 칸 — null 이면 안 본다. 고친 뒤의 그 한 줄 */
export async function updateSafetyStock(productId: string, safetyStockOrNull: number | null): Promise<SafetyStockView> {
  return putJson(`${BASE}/safety-stocks/${encodeURIComponent(productId)}`, { safety_stock: safetyStockOrNull }, SafetyStockViewSchema);
}
